import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import CheckValid from "../../Common/CheckValid";
import CommonDateTime from "../../Common/CommonDateTime";
import ChangesTable from "../common/ChangesTable";
import { Loading, ErrorState } from "../common/WarrantyStates";
import { previewPlanDraft, publishPlanDraft } from "../common/warrantyApi";
import { requestIdFor, settleRequestId } from "../common/clientRequest";
import { formatMinorDelta } from "../common/format";
import "../warranty.css";

const FIXED_NOTE = "Existing subscriptions, and quotes already given to riders, are not affected.";

const tomorrow = () => moment().add(1, "day").startOf("day").toDate();

// Shows what a publish would change, asks when it starts and why, and publishes the saved draft.
// Everything on the screen about prices and changes comes from the service's preview of the stored
// draft; nothing is worked out here.
const PublishDialog = ({
  show,
  draftId,
  revision,
  tierName,
  termsVersion,
  samples,
  scheduled,
  onClose,
  onPublished,
}) => {
  const { accessToken } = useSelector((state) => state.adminReducers);
  const [preview, setPreview] = useState({ status: "loading", data: null, message: "" });
  const [mode, setMode] = useState("now");
  const [date, setDate] = useState(null);
  const [reason, setReason] = useState("");
  const [reasonError, setReasonError] = useState("");
  const [touched, setTouched] = useState(false);
  const [inFlight, setInFlight] = useState(false);
  const [failure, setFailure] = useState(null);
  const requestRef = useRef(null);
  const mounted = useRef(true);
  // The samples in use when the dialog opens; changing them later must not reload what was typed.
  const samplesRef = useRef(samples);
  samplesRef.current = samples;
  const loadRef = useRef(null);
  // Bumped on every open, retry and close: an older preview answer must not replace the one for
  // the revision now being published.
  const previewSeq = useRef(0);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const loadPreview = useCallback(async () => {
    const mine = ++previewSeq.current;
    setPreview({ status: "loading", data: null, message: "" });
    const chosen = samplesRef.current;
    const body = chosen && chosen.length > 0 ? { samples: chosen } : {};
    const result = await previewPlanDraft(accessToken, draftId, body);
    if (!mounted.current || mine !== previewSeq.current) {
      return;
    }
    if (result.ok) {
      setPreview({ status: "ready", data: result.data, message: "" });
    } else {
      setPreview({ status: "error", data: null, message: result.message });
    }
  }, [accessToken, draftId]);

  loadRef.current = loadPreview;

  // Only an opening resets the dialog; a new token or draft id while it is open must not re-enable
  // Publish in the middle of a request.
  useEffect(() => {
    if (show) {
      setMode("now");
      setDate(null);
      setReason("");
      setReasonError("");
      setTouched(false);
      setInFlight(false);
      setFailure(null);
      requestRef.current = null;
      loadRef.current();
    } else {
      previewSeq.current += 1;
    }
  }, [show]);

  const data = preview.data;
  const currency = data?.currency || "INR";
  const issues = data?.issues || [];
  const errors = issues.filter((issue) => issue.severity === "error");
  const placeholderWarning = issues.find((issue) => issue.code === "unsellable");
  const taxWarning = issues.find((issue) => issue.code === "tax_not_default");
  const effectiveFrom = mode === "now" ? null : date ? moment(date).format("YYYY-MM-DD") : undefined;
  const reasonValid = reason.trim().length > 0 && reason.trim().length <= 500;
  const canPublish =
    preview.status === "ready" && errors.length === 0 && reasonValid && effectiveFrom !== undefined && !inFlight;

  const changeReason = (value) => {
    setReason(value);
    setFailure(null);
    CheckValid(value, { type: "warranty_reason", error: setReasonError });
  };

  const publish = async () => {
    setTouched(true);
    CheckValid(reason, { type: "warranty_reason", error: setReasonError });
    if (!canPublish) {
      return;
    }
    setInFlight(true);
    setFailure(null);
    const trimmed = reason.trim();
    const id = requestIdFor(requestRef, JSON.stringify({ draftId, revision, effectiveFrom, trimmed }));
    const result = await publishPlanDraft(accessToken, draftId, revision, effectiveFrom, trimmed, id);
    settleRequestId(requestRef, result);
    if (!mounted.current) {
      return;
    }
    setInFlight(false);
    if (result.ok) {
      toast.success(
        effectiveFrom === null
          ? `${tierName} was published and is live now.`
          : `${tierName} was published and starts on ${CommonDateTime(effectiveFrom, "LL", "show")}.`
      );
      onPublished(result.data);
      return;
    }
    const details = result.details || {};
    let message = result.message;
    if (result.code === "PLAN_SCHEDULE_AFTER" && details.effectiveFrom) {
      message = `${message} The other version starts on ${CommonDateTime(details.effectiveFrom, "LL", "show")}.`;
    }
    // The draft moved under the dialog: nothing here can succeed until it is reloaded.
    const needsReload = [
      "PLAN_DRAFT_REVISION_CONFLICT",
      "PLAN_DRAFT_STALE",
      "PLAN_DRAFT_NOT_OPEN",
      "PLAN_DRAFT_NOT_FOUND",
    ].includes(result.code);
    setFailure({
      message,
      issues: result.code === "PLAN_DRAFT_INVALID" && Array.isArray(details.issues) ? details.issues : [],
      needsReload,
    });
  };

  const close = () => {
    if (inFlight) {
      return;
    }
    previewSeq.current += 1;
    onClose({ reload: failure !== null && failure.needsReload });
  };

  return (
    <Modal
      show={show}
      onHide={close}
      backdrop={inFlight ? "static" : true}
      keyboard={!inFlight}
      className="commonModal"
      dialogClassName="warranty-publish-dialog"
      scrollable
      centered
    >
      <Modal.Header closeButton={!inFlight}>
        <Modal.Title>Publish {tierName}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {preview.status === "loading" && <Loading label="Working out what will change" />}
        {preview.status === "error" && (
          <ErrorState message={preview.message} onRetry={loadPreview} />
        )}
        {preview.status === "ready" && (
          <div className="warranty-readonly">
            {errors.length > 0 && (
              <div className="warranty-banner is-error" role="alert">
                <div>
                  <strong>The draft still has errors, so it cannot be published.</strong>
                  <ul>
                    {errors.map((issue, index) => (
                      <li key={`${issue.path}-${index}`}>
                        {issue.message}
                        {issue.path !== "" ? ` (${issue.path})` : ""}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
            {(placeholderWarning || String(termsVersion).startsWith("placeholder-")) && (
              <div className="warranty-banner is-warning" role="status">
                These are placeholder terms. This plan cannot be sold in production until real terms replace them.
              </div>
            )}
            {taxWarning && (
              <div className="warranty-banner is-warning" role="status">
                Tax rate: {taxWarning.message}
              </div>
            )}
            {scheduled.map((version) => (
              <div className="warranty-banner is-info" role="status" key={version.id}>
                A scheduled version starts on {CommonDateTime(version.effectiveFrom, "LL", "show")}: cancel it before
                publishing.
              </div>
            ))}

            <h6>What changes</h6>
            {data.liveVersionId === null && (
              <p className="warranty-help" style={{ float: "none" }}>
                This tier has no live version yet, so every field below is new.
              </p>
            )}
            <ChangesTable changes={data.changes} currency={currency} emptyText="Nothing differs from the live version." />

            <h6>Price impact on the samples</h6>
            {data.samples.every((entry) => entry.delta === null) ? (
              <p className="warranty-help" style={{ float: "none" }}>
                No comparison is available{data.liveVersionId === null ? ": there is no live version." : "."}
              </p>
            ) : (
              <table className="warranty-changes">
                <thead>
                  <tr>
                    <th scope="col">Sample</th>
                    <th scope="col">One-time price</th>
                    <th scope="col">Monthly</th>
                  </tr>
                </thead>
                <tbody>
                  {data.samples.map((entry) => (
                    <tr key={entry.sample.key}>
                      <th scope="row">{entry.sample.label}</th>
                      <td>{entry.delta ? formatMinorDelta(entry.delta.oneTimeGrossMinor, currency) : entry.error || "-"}</td>
                      <td>
                        {entry.delta
                          ? entry.delta.monthly.map((m) => (
                              <div key={m.tenureMonths}>
                                {m.tenureMonths} months: instalment {formatMinorDelta(m.instalmentGrossMinor, currency)}, first
                                payment {formatMinorDelta(m.firstPaymentGrossMinor, currency)}
                              </div>
                            ))
                          : "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <h6>When it starts</h6>
            <div className="formloginset">
              <div role="radiogroup" aria-label="When it starts" className="warranty-radios">
                <Form.Check
                  type="radio"
                  id="warranty-start-now"
                  name="warranty-start"
                  label="Now"
                  checked={mode === "now"}
                  disabled={inFlight}
                  onChange={() => setMode("now")}
                />
                <Form.Check
                  type="radio"
                  id="warranty-start-date"
                  name="warranty-start"
                  label="On a date, from tomorrow"
                  checked={mode === "date"}
                  disabled={inFlight}
                  onChange={() => setMode("date")}
                />
              </div>
              {mode === "date" && (
                <Form.Group className="mb-3">
                  <Form.Label htmlFor="warranty-start-picker">Start date</Form.Label>
                  <div className="datepickField">
                    <DatePicker
                      id="warranty-start-picker"
                      selected={date}
                      onChange={(value) => setDate(value)}
                      minDate={tomorrow()}
                      dateFormat="dd/MM/yyyy"
                      placeholderText="Choose a date"
                      autoComplete="off"
                      disabled={inFlight}
                    />
                  </div>
                </Form.Group>
              )}
              <p className="warranty-dialog-hint">{FIXED_NOTE}</p>
              <Form.Group className="mb-1">
                <Form.Label htmlFor="warrantyPublishReason">Reason</Form.Label>
                <Form.Control
                  id="warrantyPublishReason"
                  as="textarea"
                  value={reason}
                  maxLength={500}
                  placeholder="Say why this is being published (kept in the audit log)"
                  disabled={inFlight}
                  onChange={(event) => changeReason(event.target.value)}
                  onBlur={() => setTouched(true)}
                />
                {touched && reasonError && <div className="danger-colorset">{reasonError}</div>}
              </Form.Group>
            </div>

            {failure && (
              <div className="warranty-dialog-error" role="alert">
                {failure.message}
                {failure.issues.length > 0 && (
                  <ul>
                    {failure.issues.map((issue, index) => (
                      <li key={`${issue.path}-${index}`}>
                        {issue.message}
                        {issue.path !== "" ? ` (${issue.path})` : ""}
                      </li>
                    ))}
                  </ul>
                )}
                {failure.needsReload && <div>Close this window; the draft will be reloaded.</div>}
              </div>
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={close} disabled={inFlight}>
          {failure && failure.needsReload ? "Close and reload" : "Cancel"}
        </Button>
        <Button className="addbtncmn" onClick={publish} disabled={!canPublish}>
          {inFlight
            ? "Publishing"
            : mode === "now"
            ? "Publish now"
            : date
            ? `Publish for ${CommonDateTime(date, "LL", "show")}`
            : "Publish"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default PublishDialog;
